import { Dialog } from '@headlessui/react';
import { ClassroomGroupDto, useDialog } from '@ecdlink/core';
import {
  ActionModal,
  Alert,
  AttendanceListDataItem,
  AttendanceStatus,
  BannerWrapper,
  Button,
  DialogPosition,
  FilterInfo,
  renderIcon,
  SearchDropDown,
  SearchDropDownOption,
  StatusChip,
  Typography,
} from '@ecdlink/ui';
import { format, getDay } from 'date-fns';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@store';
import { analyticsActions } from '@store/analytics';
import { attendanceActions, attendanceThunkActions } from '@store/attendance';
import { ChildAttendance } from '@store/attendance/attendance.types';
import { classroomsSelectors } from '@store/classroom';
import { userSelectors } from '@store/user';
import {
  classroomGroupHasAttendanceOnDate,
  getAttendanceStatusCheck,
  getPlaygroups,
  mapTrackAttendance,
} from '@utils/classroom/attendance/track-attendance-utils';
import { NoPlaygroupClassroomType } from '@/enums/ProgrammeType';
import { practitionerSelectors } from '@/store/practitioner';
import { childrenSelectors } from '@store/children';
import {
  AttendanceListProps,
  AttendanceState,
} from '../../components/attendance-list/attendance-list.types';
import * as styles from '../../../attendance/components/attendance-list/attendance-list.styles';
import EditClassProgrammeAttendanceList from '../class-programme-attendance-list/class-programme-attendance-list';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

export const filterInfo: FilterInfo = {
  filterName: 'Class',
  filterHint: 'You can select multiple classes',
};

export const EditRegistersAttendanceList: React.FC<AttendanceListProps> = ({
  submitText = '',
  attendanceDate = new Date(),
  onSubmitSuccess,
  editAttendanceRegisterVisible,
  classroomgroupId,
  filterGroups,
  onBack,
  classroomName,
  selectedRegister,
  selectedAttendanceRegisters,
  setEditAttendanceRegisterVisible,
}) => {
  const appDispatch = useAppDispatch();
  const { isOnline } = useOnlineStatus();
  const [presentChildrenCount, setPresentChildrenCount] = useState<number>(0);
  const [absentChildrenCount, setAbsentChildrenCount] = useState<number>(0);
  const [hasChildren, setHasChildren] = useState<boolean>(false);
  const [attendanceGroups, setAttendanceGroups] = useState<AttendanceState[]>();
  const [updatedFilterGroups, setUpdatedFilterGroups] = useState<
    ClassroomGroupDto[]
  >([]);
  const [filterDropdownOptions, setFilterDropdownOptions] = useState<
    SearchDropDownOption<any>[]
  >([]);
  const [selectedClassroomGroups, setSelectedClassroomGroups] = useState<
    ClassroomGroupDto[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const userData = useSelector(userSelectors.getUser);
  const practitioner = useSelector(practitionerSelectors.getPractitioner);
  const allLearners = useSelector(
    classroomsSelectors.getClassroomGroupLearnersForAttendance
  );
  const children = useSelector(childrenSelectors.getChildren);
  const user = useSelector(userSelectors.getUser);
  const allClassroomGroups = useSelector(
    classroomsSelectors.getClassroomGroups
  );

  const isPrincipal = practitioner?.isPrincipal === true;

  const dialog = useDialog();

  const classroomGroups = allClassroomGroups.filter(
    (x) => x.name !== NoPlaygroupClassroomType.name
  );

  const classroomGroupsForPrincipal = classroomGroups.filter(
    (item) => item?.userId === userData?.id
  );
  const classProgrammes = useSelector(classroomsSelectors.getClassProgrammes);
  const classProgrammesForPrincipal = classProgrammes.filter((el) => {
    return classroomGroupsForPrincipal.some((f) => {
      return f.id === el.classroomGroupId;
    });
  });
  const classProgrammesUpdated = isPrincipal
    ? classProgrammesForPrincipal
    : classProgrammes;

  const _allLearners = allLearners.filter((x) => {
    const child = children?.find((c) => c.userId === x.userId);
    const startedAttendance = new Date(x.startedAttendance);
    startedAttendance.setHours(0, 0, 0, 0);
    return (
      child &&
      child.caregiverId &&
      (!Boolean(x.stoppedAttendance) ||
        new Date(x.stoppedAttendance!).getTime() >= attendanceDate.getTime()) &&
      attendanceDate.getTime() >= new Date(startedAttendance).getTime()
    );
  });

  const classroomGroupsUpdated = isPrincipal
    ? classroomGroupsForPrincipal.filter((x) =>
        classProgrammesUpdated.some((p) => {
          return (
            p.classroomGroupId === x.id &&
            p.meetingDay === getDay(attendanceDate)
          );
        })
      )
    : classroomGroups.filter((x) =>
        classProgrammesUpdated.some((p) => {
          return (
            p.classroomGroupId === x.id &&
            p.meetingDay === getDay(attendanceDate)
          );
        })
      );

  const uniqueLearners = _allLearners.filter((object, index, array) => {
    return (
      index ===
      array.findIndex((newObject) => newObject.userId === object.userId)
    );
  });

  const groupsWithLearners = classroomGroupsUpdated.filter((x) =>
    _allLearners.some((f) => {
      return f.classroomGroupId === x.id;
    })
  );

  const primaryClassProgramme = classProgrammesUpdated.filter(
    (prog) =>
      prog.meetingDay === getDay(attendanceDate) &&
      groupsWithLearners.some((f) => {
        return f.id === prog.classroomGroupId;
      })
  );

  useEffect(() => {
    if (filterGroups && filterGroups.length > 0) {
      const meetingDay = getDay(attendanceDate);

      const matchingGroups = filterGroups
        .filter(
          (ag) =>
            ag.classProgramme?.meetingDay === meetingDay &&
            ag.classroomGroup != null
        )
        .map((ag) => ag.classroomGroup);

      const uniqueGroups = matchingGroups.filter(
        (group, index, self) =>
          index === self.findIndex((g) => g.id === group.id)
      );

      setUpdatedFilterGroups(uniqueGroups);
    } else {
      setUpdatedFilterGroups([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterGroups]);

  useEffect(() => {
    const options = updatedFilterGroups.map((x) => ({
      id: x.id ?? '',
      value: x,
      label: x.name,
      disabled: false,
    }));
    setFilterDropdownOptions(options);
  }, [updatedFilterGroups]);

  useEffect(() => {
    if (classroomGroups) {
      setHasChildren(uniqueLearners && uniqueLearners.length > 0);
      setSelectedClassroomGroups(
        classroomgroupId
          ? groupsWithLearners?.filter((item) => item?.id === classroomgroupId)
          : groupsWithLearners
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateAttendanceState = (attendanceGroups: AttendanceState[]) => {
    const attendanceStatusCheck = getAttendanceStatusCheck(
      attendanceGroups,
      hasChildren
    );
    setPresentChildrenCount(attendanceStatusCheck.presentCount);
    setAbsentChildrenCount(attendanceStatusCheck.absentCount);
  };

  useEffect(() => {
    if (selectedClassroomGroups.length > 0)
      updateAttendanceState(attendanceGroups || []);
    else updateAttendanceState([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceGroups, selectedClassroomGroups]);

  const onFilterItemsChanges = (value: SearchDropDownOption<any>[]) => {
    setSelectedClassroomGroups(value.map((x) => x.value));

    const updatedAttendanceGroups =
      attendanceGroups?.filter((attendanceGroup) =>
        value.some(
          (classroomGroup) =>
            classroomGroup.value.id === attendanceGroup.cacheId
        )
      ) ?? [];

    setAttendanceGroups(updatedAttendanceGroups);
    updateAttendanceState(updatedAttendanceGroups);
  };

  // ValidateAttendanceList is only called when a new class is added.
  // This function and useEffect are needed for when a class is removed.
  useEffect(() => {
    filterAttendanceGroups(selectedClassroomGroups);
  }, [selectedClassroomGroups]);

  const filterAttendanceGroups = (selectedGroups: ClassroomGroupDto[]) => {
    setAttendanceGroups((prevGroups) => {
      if (!prevGroups) return [];

      let newGroups = prevGroups.filter((group) =>
        selectedGroups.some(
          (selectedGroup) => selectedGroup.id === group.cacheId
        )
      );

      if (!newGroups.some((group) => group.isRequired)) {
        const primaryList = prevGroups.find((group) => group.isRequired);
        if (primaryList) newGroups.push(primaryList);
      }

      return newGroups;
    });
  };

  const validateAttendanceList = (
    attendanceListId: string,
    updateList: AttendanceListDataItem[],
    isPrimaryList: boolean,
    selectedGroups: ClassroomGroupDto[]
  ) => {
    setAttendanceGroups((prevGroups) => {
      let newAttendanceGroups = [...(prevGroups || [])];
      const groupIndex = newAttendanceGroups.findIndex(
        (x) => x.cacheId === attendanceListId
      );

      if (groupIndex === -1) {
        newAttendanceGroups.push({
          cacheId: attendanceListId,
          isRequired: isPrimaryList,
          list: updateList,
        });
      } else {
        newAttendanceGroups[groupIndex] = {
          cacheId: attendanceListId,
          isRequired: isPrimaryList,
          list: updateList,
        };
      }

      return newAttendanceGroups;
    });
  };

  const handleFormSubmit = async () => {
    const currentClassProgramme = classroomGroupHasAttendanceOnDate(
      classProgrammesUpdated,
      attendanceDate,
      editAttendanceRegisterVisible ? classroomgroupId : ''
    );

    const currentGroup = classroomGroups.find(
      (x) => x.id === currentClassProgramme?.classroomGroupId
    );

    if (!currentGroup) return;

    const currentAttendanceGroups = attendanceGroups?.filter((group) =>
      selectedClassroomGroups.some(
        (selectedGroup) => selectedGroup.id === group.cacheId
      )
    );

    if (!currentAttendanceGroups) return;

    const currentProgrammes = getPlaygroups(
      classProgrammesUpdated,
      attendanceDate,
      selectedClassroomGroups
        .map((group) => group.id)
        .filter((id): id is string => id !== undefined) // Ensures only valid strings are included
    );

    if (!currentProgrammes || currentProgrammes.length === 0) return;

    if (attendanceGroups) {
      for (let i = 0; i < attendanceGroups.length; i++) {
        const programme: AttendanceState[] = [];

        programme.push(attendanceGroups[i]);

        const attendanceGroupList = programme?.reduce((prev, curr) => {
          return [...prev, ...curr.list];
        }, [] as AttendanceListDataItem[]);

        const attendanceGroupOfClassroomGroup = attendanceGroupList?.filter(
          (object, index, array) => {
            return (
              index ===
              array.findIndex(
                (newObject) => newObject.attenendeeId === object.attenendeeId
              )
            );
          }
        );

        const allAttendedChildren: ChildAttendance[] =
          attendanceGroupOfClassroomGroup?.map((x) => ({
            userId: x.attenendeeId,
            attended: x.status === AttendanceStatus.Present,
          })) || [];

        const trackAttendanceInput = mapTrackAttendance(
          user?.id || '',
          allAttendedChildren,
          new Date(
            attendanceDate.getFullYear(),
            attendanceDate.getMonth(),
            attendanceDate.getDate(),
            12,
            0,
            0
          ).toISOString(),
          currentProgrammes[i].id ?? '',
          true
        );

        setIsLoading(true);

        await appDispatch(
          attendanceActions.trackAttendance(trackAttendanceInput)
        );
        if (isOnline) {
          const attendanceResult = await appDispatch(
            attendanceThunkActions.trackAttendanceSync(trackAttendanceInput)
          );

          if (Array.isArray(attendanceResult?.payload)) {
            if (attendanceResult.payload[0] === false) {
              await appDispatch(
                attendanceActions.removeAttendance(trackAttendanceInput)
              );
              await appDispatch(
                attendanceActions.removeTrackedAttendance(trackAttendanceInput)
              );
              aheadDateModal();
            }
          }
        }

        await appDispatch(
          analyticsActions.createEventTracking({
            action: 'Attendance tracking click',
            category: 'Attendance tracking click',
          })
        );
        setIsLoading(false);

        onSubmitSuccess &&
          onSubmitSuccess({
            attendanceDate,
            classroomGroupId: programme[0].cacheId,
          });
        setAttendanceGroups([]);
        setSelectedClassroomGroups([]);
        updateAttendanceState([]);
        setEditAttendanceRegisterVisible &&
          setEditAttendanceRegisterVisible(false);
      }
    } else return;
  };

  const aheadDateModal = () => {
    dialog({
      position: DialogPosition.Middle,
      render: (onSubmit, onCancel) => (
        <Dialog.Panel>
          <ActionModal
            icon={'InformationCircleIcon'}
            iconColor="alertMain"
            iconBorderColor="alertBg"
            importantText={`Your device's date is ahead of the current date.`}
            detailText={'Your attendance register cannot be submitted.'}
            actionButtons={[
              {
                text: 'Exit',
                textColour: 'white',
                colour: 'primary',
                type: 'filled',
                onClick: () => {
                  onCancel();
                },
                leadingIcon: 'ArrowLeftIcon',
              },
            ]}
          />
        </Dialog.Panel>
      ),
    });
  };

  const submitPrompt = () => {
    dialog({
      position: DialogPosition.Middle,
      render: (onSubmit, onCancel) => (
        <Dialog.Panel>
          <ActionModal
            textAlignment="center"
            icon={'InformationCircleIcon'}
            iconColor="alertMain"
            iconBorderColor="alertBg"
            importantText={`Are you sure you want to submit your ${format(
              attendanceDate,
              'EEEE, d LLLL'
            )} attendance register?`}
            customDetailText={
              <Typography
                type="h4"
                className="mb-7 mt-4"
                text={`By submitting this register, you confirm that all the attendance information is accurate. `}
                color="black"
                align="center"
              />
            }
            detailText={'Your signature will be added to the monthly register.'}
            actionButtons={[
              {
                text: 'Yes, submit register',
                textColour: 'white',
                colour: 'primary',
                type: 'filled',
                onClick: () => {
                  handleFormSubmit();
                  onCancel();
                },
                leadingIcon: 'SaveIcon',
              },
              {
                text: 'No, continue editing',
                textColour: 'primary',
                colour: 'primary',
                type: 'outlined',
                onClick: () => onCancel(),
                leadingIcon: 'PencilIcon',
              },
            ]}
          />
        </Dialog.Panel>
      ),
    });
  };

  return (
    <div className={styles.wrapper}>
      <BannerWrapper
        size={'small'}
        showBackground={false}
        color={'primary'}
        onBack={onBack}
        title={`Edit ${classroomName} Register`}
        subTitle={format(attendanceDate, 'EEEE, d LLLL')}
        // className={styles.bannerContentWrapper}
        // displayOffline={!isOnline}
      >
        <div className={'bg-uiBg flex w-full flex-col items-start'}>
          {((practitioner?.isPrincipal &&
            classroomGroupsForPrincipal.length > 1) ||
            (!practitioner?.isPrincipal && classroomGroups?.length > 1)) && (
            <>
              <SearchDropDown<any>
                displayMenuOverlay
                menuItemClassName={styles.dropdownStyles}
                className={'mr-1 ml-2'}
                options={
                  filterDropdownOptions.length > 0
                    ? filterDropdownOptions
                    : groupsWithLearners
                    ? groupsWithLearners.map((x) => ({
                        id: x.id ?? '',
                        value: x,
                        label: x.name,
                        disabled: false,
                      }))
                    : []
                }
                onChange={(value) => onFilterItemsChanges(value)}
                placeholder={'Class'}
                pluralSelectionText={'Classes'}
                color={'secondary'}
                multiple
                selectedOptions={selectedClassroomGroups.map((x) => {
                  return {
                    id: x.id ?? '',
                    value: x,
                    label: x.name,
                  };
                })}
                info={{
                  name: `Filter by:${filterInfo?.filterName}`,
                  hint: filterInfo?.filterHint || '',
                }}
              />
            </>
          )}
        </div>
        <div>
          {hasChildren ? (
            <>
              <div>
                <div className={styles.statusChipsWrapper(false)}>
                  <StatusChip
                    className={'mr-2 '}
                    padding={'px-3 py-3'}
                    textColour="successMain"
                    borderColour="white"
                    textType="h2"
                    backgroundColour="white"
                    text={`${presentChildrenCount} present`}
                  />
                  <div>
                    <StatusChip
                      textColour="errorMain"
                      padding={'px-3 py-3'}
                      borderColour="white"
                      textType="h2"
                      backgroundColour="white"
                      text={`${absentChildrenCount} absent`}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.attendanceListsWrapper}>
                {selectedClassroomGroups.map((selectedGroup, idx) => {
                  const isPrimaryList =
                    selectedGroup.id ===
                    primaryClassProgramme[0]?.classroomGroupId;
                  return (
                    <div
                      key={`attencance_list_${idx}`}
                      id={`attendanceList${selectedGroup.id}`}
                    >
                      <EditClassProgrammeAttendanceList
                        isPrimaryClass={isPrimaryList}
                        classroomGroup={selectedGroup}
                        attendanceDate={attendanceDate}
                        isMultipleClasses={selectedClassroomGroups.length > 1}
                        onAttendanceUpdated={(state) => {
                          validateAttendanceList(
                            selectedGroup.id || '',
                            state.listItems,
                            isPrimaryList,
                            selectedClassroomGroups
                          );
                        }}
                        id={`attendance-list${selectedGroup.id}`}
                        selectedAttendanceRegisters={
                          selectedAttendanceRegisters
                        }
                      />
                    </div>
                  );
                })}

                <div className={'px-4'}>
                  <Button
                    id="gtm-add-attendance"
                    onClick={submitPrompt}
                    className="mt-4 w-full"
                    size="small"
                    color="primary"
                    type="filled"
                    disabled={selectedClassroomGroups.length === 0 || isLoading}
                    isLoading={isLoading}
                  >
                    {renderIcon('PaperAirplaneIcon', 'h-5 w-5 text-white')}
                    <Typography
                      type="h6"
                      className="ml-2"
                      text={
                        submitText.length > 0
                          ? submitText
                          : 'Submit today’s register'
                      }
                      color="white"
                    />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className={'mb-2 flex h-full flex-1 flex-col gap-4 px-4 pt-4'}>
              <Alert
                title={'There are no children in your class(es).'}
                message={
                  'Add children to your class(es) to capture attendance.'
                }
                type={'info'}
              />
            </div>
          )}
        </div>
      </BannerWrapper>
    </div>
  );
};

export default EditRegistersAttendanceList;
